import type { WithId } from '@medplum/core';
import type { Observation } from '@medplum/fhirtypes';
import { parseISO } from 'date-fns';
import type { ObservationProvenance } from './observationProvenance';
import { resolveObservationProvenance } from './observationProvenance';
import { preferImperialQuantity } from './quantity';
import { observationResultCodes } from './resultRecords';

/**
 * A single point on a measurement trend line. `y2` carries the second value of composite readings
 * (e.g. BP diastolic). `code` is the specific result code the reading carried, which on a row holding
 * several codes of one concept is what tells a fasting glucose from a random one; `codeDisplay` is
 * what the writer called that code, the only name a sibling vital code has (the curated display map
 * covers labs only); `provenance` is where it came from. All are optional so a chart fed hand-written
 * points still type-checks.
 */
export type TrendPoint = {
  x: number;
  y: number;
  y2?: number;
  code?: string;
  codeDisplay?: string;
  provenance?: ObservationProvenance;
};

/** A point before the no-value readings are filtered out. */
type DraftTrendPoint = Omit<TrendPoint, 'y'> & { y: number | null };

// Sleep durations are stored in minutes by every producer, so plotting the raw stored value keeps a
// mixed-producer series on one scale. Non-sleep measurements likewise plot their raw stored value.
const parseTrendValues = (observation: Observation): { y: number | null; y2?: number } => {
  const value = preferImperialQuantity(observation.valueQuantity)?.value;
  if (value !== undefined) return { y: value };

  const componentValues = observation.component
    ?.map((c) => preferImperialQuantity(c.valueQuantity)?.value)
    .filter((v): v is number => v !== undefined);

  return { y: componentValues?.[0] ?? null, y2: componentValues?.[1] };
};

/**
 * What the writer called the code this reading is filed under — the matching coding's `display`,
 * falling back to the concept's own `text` and then to any coding that carries a name. An imported
 * clinic weight arrives as "Body weight Measured" and nothing else names `3141-9`, so this is what a
 * tooltip has to work with when the reading is coded a sibling of the code its row tracks.
 */
const resultCodeDisplay = (observation: Observation, code: string | undefined): string | undefined => {
  const codings = observation.code?.coding ?? [];
  return (
    codings.find((c) => c.code === code && c.display)?.display ??
    observation.code?.text ??
    codings.find((c) => c.display)?.display
  );
};

/**
 * Build sorted numeric trend points from observations, preferring imperial units and handling
 * composite (e.g. blood-pressure) component values. Shared by patient-web's sparkline and detailed
 * chart and by patient-native's chart.
 */
export const toTrendSeries = (results: WithId<Observation>[]): { data: TrendPoint[]; hasSecondLine: boolean } => {
  const data = results
    .map((r): DraftTrendPoint => {
      const code = observationResultCodes(r)[0];
      return {
        x: parseISO(r.effectiveDateTime ?? '').getTime(),
        ...parseTrendValues(r),
        code,
        codeDisplay: resultCodeDisplay(r, code),
        provenance: resolveObservationProvenance(r),
      };
    })
    .filter((p): p is TrendPoint => p.y !== null && !isNaN(p.x))
    .sort((a, b) => a.x - b.x);
  return { data, hasSecondLine: data.some((p) => p.y2 !== undefined) };
};
